/**
 * Report Variant editor — admin creates/edits a saved report and assigns access.
 */
import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const NUMERIC = new Set(["number", "decimal", "currency"]);

const blank = () => ({
    name: "", description: "", kind: "single",
    form_id: "", group_by: "__created_day__",
    metric_op: "count", metric_field: null, chart_type: "bar",
    joins: [], multi_group_by: [], multi_metrics: [{ op: "count", form: "primary" }], multi_filters: [],
    access_user_ids: [], access_group_ids: [], access_role_ids: [],
});

export function ReportVariantDialog({ open, onOpenChange, initial, onSaved }) {
    const [form, setForm] = useState(blank());
    const [forms, setForms] = useState([]);
    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [roles, setRoles] = useState([]);
    const [summary, setSummary] = useState(null);
    const [saving, setSaving] = useState(false);
    const editing = initial && initial.id;

    useEffect(() => {
        if (!open) return;
        setForm({ ...blank(), ...(initial || {}) });
        Promise.all([api.get("/forms"), api.get("/users"), api.get("/groups"), api.get("/roles")])
            .then(([f, u, g, r]) => { setForms(f.data); setUsers(u.data); setGroups(g.data); setRoles(r.data); })
            .catch((e) => toast.error(extractError(e)));
    }, [open, initial]);

    // Load fields for selected form
    useEffect(() => {
        if (!form.form_id) { setSummary(null); return; }
        (async () => {
            try {
                const { data } = await api.get(`/reports/${form.form_id}/summary`);
                setSummary(data);
            } catch (e) { /* ignore — admin may switch forms */ setSummary(null); }
        })();
    }, [form.form_id]);

    const numericFields = useMemo(
        () => (summary?.available_fields || []).filter((f) => NUMERIC.has(f.type)),
        [summary],
    );

    const toggle = (k, id) => {
        const arr = form[k] || [];
        setForm({ ...form, [k]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] });
    };

    const save = async () => {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        if (!form.form_id) { toast.error("Pick a primary form"); return; }
        if (form.kind === "single") {
            if (!form.group_by) { toast.error("Pick a group-by field"); return; }
            if (form.metric_op !== "count" && !form.metric_field) {
                toast.error("Metric field is required for sum/avg"); return;
            }
        } else {
            // multi
            if (!(form.multi_metrics || []).length) {
                toast.error("Add at least one metric"); return;
            }
            const bad = (form.multi_metrics || []).find((m) => m.op !== "count" && !m.field);
            if (bad) { toast.error("Every non-count metric needs a field"); return; }
        }
        setSaving(true);
        try {
            const payload = { ...form };
            if (form.kind === "single") {
                payload.metric_field = form.metric_op === "count" ? null : form.metric_field;
                payload.joins = []; payload.multi_group_by = []; payload.multi_metrics = []; payload.multi_filters = [];
            } else {
                payload.group_by = null; payload.metric_field = null;
                payload.joins = (form.joins || []).map(({ alias, lookup_field, target_form_id }) => ({ alias, lookup_field, target_form_id }));
                payload.multi_group_by = (form.multi_group_by || []).map(({ form: ff, field }) => ({ form: ff, field }));
                payload.multi_metrics = (form.multi_metrics || []).map(({ op, form: ff, field, label }) => ({ op, form: ff, field, label }));
                payload.multi_filters = (form.multi_filters || []).map(({ form: ff, field, op, value }) => ({ form: ff, field, op, value }));
            }
            if (editing) {
                await api.patch(`/report-variants/${initial.id}`, payload);
                toast.success("Report updated");
            } else {
                await api.post("/report-variants", payload);
                toast.success("Report created");
            }
            onSaved?.();
            onOpenChange(false);
        } catch (e) { toast.error(extractError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="report-variant-dialog">
                <DialogHeader>
                    <DialogTitle>{editing ? `Edit "${initial.name}"` : "New report"}</DialogTitle>
                    <DialogDescription>Configure a saved report and assign who can see it.</DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="config" className="w-full">
                    <TabsList className="rounded-sm mb-4">
                        <TabsTrigger value="config" data-testid="rv-tab-config">Configuration</TabsTrigger>
                        <TabsTrigger value="access" data-testid="rv-tab-access">Access</TabsTrigger>
                    </TabsList>

                    <TabsContent value="config" className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-overline">Name</Label>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm" data-testid="rv-name-input" />
                            </div>
                            <div>
                                <Label className="label-overline">Primary form</Label>
                                <Select value={form.form_id} onValueChange={(v) => setForm({ ...form, form_id: v, group_by: "__created_day__", metric_field: null, joins: [], multi_group_by: [], multi_filters: [] })}>
                                    <SelectTrigger className="mt-2 rounded-sm" data-testid="rv-form-select"><SelectValue placeholder="Pick a form" /></SelectTrigger>
                                    <SelectContent>
                                        {forms.map((f) => <SelectItem key={f.id} value={f.id}>{f.form_name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label className="label-overline">Description</Label>
                            <Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-2 rounded-sm" data-testid="rv-desc-input" />
                        </div>

                        <div className="brut-section p-3 bg-secondary/30 flex items-center justify-between" data-testid="rv-kind-row">
                            <div>
                                <Label className="label-overline">Mode</Label>
                                <p className="text-[11px] text-muted-foreground mt-1">Single-form is a quick group-by chart. Multi-form joins data across linked forms via Lookup fields.</p>
                            </div>
                            <div className="flex gap-2">
                                <Button type="button" size="sm" variant={form.kind === "single" ? "default" : "outline"} className={`rounded-sm ${form.kind === "single" ? "bg-primary text-primary-foreground" : ""}`} onClick={() => setForm({ ...form, kind: "single" })} data-testid="rv-kind-single">Single-form</Button>
                                <Button type="button" size="sm" variant={form.kind === "multi" ? "default" : "outline"} className={`rounded-sm ${form.kind === "multi" ? "bg-primary text-primary-foreground" : ""}`} onClick={() => setForm({ ...form, kind: "multi" })} data-testid="rv-kind-multi">Multi-form</Button>
                            </div>
                        </div>

                        {form.kind === "single" ? (
                            <SingleFormConfig form={form} setForm={setForm} summary={summary} numericFields={numericFields} />
                        ) : (
                            <MultiFormConfig form={form} setForm={setForm} forms={forms} />
                        )}
                    </TabsContent>

                    <TabsContent value="access" className="space-y-4">
                        <p className="text-xs text-muted-foreground">
                            Grant access by user, group or role. Admins always see all reports.
                        </p>
                        <Tabs defaultValue="groups">
                            <TabsList className="rounded-sm">
                                <TabsTrigger value="users" data-testid="rv-access-users-tab">Users ({form.access_user_ids?.length || 0})</TabsTrigger>
                                <TabsTrigger value="groups" data-testid="rv-access-groups-tab">Groups ({form.access_group_ids?.length || 0})</TabsTrigger>
                                <TabsTrigger value="roles" data-testid="rv-access-roles-tab">Roles ({form.access_role_ids?.length || 0})</TabsTrigger>
                            </TabsList>
                            <TabsContent value="users">
                                <div className="border border-border rounded-sm max-h-64 overflow-y-auto">
                                    {users.length === 0 && <div className="p-3 text-xs text-muted-foreground">No users</div>}
                                    {users.map((u) => (
                                        <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm border-b border-border last:border-b-0 cursor-pointer hover:bg-secondary/30">
                                            <Checkbox checked={form.access_user_ids?.includes(u.id)} onCheckedChange={() => toggle("access_user_ids", u.id)} data-testid={`rv-user-${u.id}`} />
                                            <span className="truncate">{u.name} — <span className="font-mono text-xs text-muted-foreground">{u.email}</span></span>
                                        </label>
                                    ))}
                                </div>
                            </TabsContent>
                            <TabsContent value="groups">
                                <div className="border border-border rounded-sm max-h-64 overflow-y-auto">
                                    {groups.length === 0 && <div className="p-3 text-xs text-muted-foreground">No groups</div>}
                                    {groups.map((g) => (
                                        <label key={g.id} className="flex items-center gap-2 px-3 py-2 text-sm border-b border-border last:border-b-0 cursor-pointer hover:bg-secondary/30">
                                            <Checkbox checked={form.access_group_ids?.includes(g.id)} onCheckedChange={() => toggle("access_group_ids", g.id)} data-testid={`rv-group-${g.id}`} />
                                            <span className="truncate">{g.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </TabsContent>
                            <TabsContent value="roles">
                                <div className="border border-border rounded-sm max-h-64 overflow-y-auto">
                                    {roles.length === 0 && <div className="p-3 text-xs text-muted-foreground">No roles</div>}
                                    {roles.map((r) => (
                                        <label key={r.id} className="flex items-center gap-2 px-3 py-2 text-sm border-b border-border last:border-b-0 cursor-pointer hover:bg-secondary/30">
                                            <Checkbox checked={form.access_role_ids?.includes(r.id)} onCheckedChange={() => toggle("access_role_ids", r.id)} data-testid={`rv-role-${r.id}`} />
                                            <span className="font-mono text-xs">{r.code}</span><span className="text-muted-foreground">— {r.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </TabsContent>
                        </Tabs>
                    </TabsContent>
                </Tabs>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
                    <Button onClick={save} disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="rv-save-btn">
                        {saving ? "Saving…" : (editing ? "Save changes" : "Create report")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// -------- Single-form config (legacy) --------
function SingleFormConfig({ form, setForm, summary, numericFields }) {
    return (
        <>
            <div className="grid grid-cols-3 gap-3">
                <div>
                    <Label className="label-overline">Group by</Label>
                    <Select value={form.group_by || "__created_day__"} onValueChange={(v) => setForm({ ...form, group_by: v })} disabled={!summary}>
                        <SelectTrigger className="mt-2 rounded-sm" data-testid="rv-groupby-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__created_day__">Submission day</SelectItem>
                            {(summary?.available_fields || []).map((f) => (
                                <SelectItem key={f.name} value={f.name}>{f.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="label-overline">Metric</Label>
                    <Select value={form.metric_op} onValueChange={(v) => setForm({ ...form, metric_op: v, metric_field: v === "count" ? null : form.metric_field })}>
                        <SelectTrigger className="mt-2 rounded-sm" data-testid="rv-metric-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="count">Count</SelectItem>
                            <SelectItem value="sum" disabled={numericFields.length === 0}>Sum</SelectItem>
                            <SelectItem value="avg" disabled={numericFields.length === 0}>Average</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="label-overline">Chart</Label>
                    <Select value={form.chart_type} onValueChange={(v) => setForm({ ...form, chart_type: v })}>
                        <SelectTrigger className="mt-2 rounded-sm" data-testid="rv-chart-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="bar">Bar</SelectItem>
                            <SelectItem value="line">Line</SelectItem>
                            <SelectItem value="pie">Pie</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            {form.metric_op !== "count" && (
                <div>
                    <Label className="label-overline">Numeric field</Label>
                    <Select value={form.metric_field || ""} onValueChange={(v) => setForm({ ...form, metric_field: v })}>
                        <SelectTrigger className="mt-2 rounded-sm" data-testid="rv-metric-field-select"><SelectValue placeholder="Pick a numeric field" /></SelectTrigger>
                        <SelectContent>
                            {numericFields.length === 0 && <SelectItem value="__none__" disabled>No numeric fields</SelectItem>}
                            {numericFields.map((f) => <SelectItem key={f.name} value={f.name}>{f.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            )}
        </>
    );
}

// -------- Multi-form config --------
function MultiFormConfig({ form, setForm, forms }) {
    const [primaryFields, setPrimaryFields] = useState([]);
    const [joinFieldsMap, setJoinFieldsMap] = useState({});

    useEffect(() => {
        if (!form.form_id) { setPrimaryFields([]); return; }
        (async () => {
            try {
                const { data } = await api.get(`/forms/${form.form_id}`);
                setPrimaryFields(data.fields || []);
            } catch { setPrimaryFields([]); }
        })();
    }, [form.form_id]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const next = {};
            for (const j of (form.joins || [])) {
                if (!j.target_form_id) continue;
                try {
                    const { data } = await api.get(`/lookup/forms/${j.target_form_id}/fields`);
                    next[j.alias] = data || [];
                } catch { next[j.alias] = []; }
            }
            if (!cancelled) setJoinFieldsMap(next);
        })();
        return () => { cancelled = true; };
    }, [form.joins]);

    const lookupFields = primaryFields.filter((f) => f.field_type === "lookup" && f.lookup_form_id);
    const sources = useMemo(() => {
        const out = [{ value: "primary", label: "Primary form" }];
        (form.joins || []).forEach((j) => out.push({ value: j.alias, label: `→ ${j.alias}` }));
        return out;
    }, [form.joins]);

    const fieldsForSource = (sourceKey) => sourceKey === "primary" ? primaryFields : (joinFieldsMap[sourceKey] || []);

    // Client-only per-row identity so React reconciliation stays stable when
    // items are added/removed. Stripped implicitly by the save() destructure.
    const _uid = () => `rv-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

    const addJoin = () => {
        const used = new Set((form.joins || []).map((j) => j.lookup_field));
        const candidate = lookupFields.find((f) => !used.has(f.field_name));
        if (!candidate) { toast.error("No more lookup fields available on the primary form"); return; }
        const alias = candidate.field_name;
        setForm({
            ...form,
            joins: [...(form.joins || []), { __uid: _uid(), alias, lookup_field: candidate.field_name, target_form_id: candidate.lookup_form_id }],
        });
    };
    const removeJoin = (idx) => {
        const j = (form.joins || [])[idx];
        setForm({
            ...form,
            joins: (form.joins || []).filter((_, i) => i !== idx),
            multi_group_by: (form.multi_group_by || []).filter((d) => d.form !== j.alias),
            multi_metrics: (form.multi_metrics || []).filter((m) => m.form !== j.alias),
            multi_filters: (form.multi_filters || []).filter((f) => f.form !== j.alias),
        });
    };

    const addDim = () => {
        if ((form.multi_group_by || []).length >= 2) { toast.error("Max 2 dimensions"); return; }
        setForm({ ...form, multi_group_by: [...(form.multi_group_by || []), { __uid: _uid(), form: "primary", field: "__created_day__" }] });
    };
    const setDim = (idx, patch) => {
        const next = [...(form.multi_group_by || [])];
        next[idx] = { ...next[idx], ...patch };
        setForm({ ...form, multi_group_by: next });
    };
    const removeDim = (idx) => setForm({ ...form, multi_group_by: (form.multi_group_by || []).filter((_, i) => i !== idx) });

    const addMetric = () => setForm({ ...form, multi_metrics: [...(form.multi_metrics || []), { __uid: _uid(), op: "count", form: "primary" }] });
    const setMetric = (idx, patch) => {
        const next = [...(form.multi_metrics || [])];
        next[idx] = { ...next[idx], ...patch };
        setForm({ ...form, multi_metrics: next });
    };
    const removeMetric = (idx) => setForm({ ...form, multi_metrics: (form.multi_metrics || []).filter((_, i) => i !== idx) });

    const addFilter = () => setForm({ ...form, multi_filters: [...(form.multi_filters || []), { __uid: _uid(), form: "primary", field: "", op: "eq", value: "" }] });
    const setFilter = (idx, patch) => {
        const next = [...(form.multi_filters || [])];
        next[idx] = { ...next[idx], ...patch };
        setForm({ ...form, multi_filters: next });
    };
    const removeFilter = (idx) => setForm({ ...form, multi_filters: (form.multi_filters || []).filter((_, i) => i !== idx) });

    return (
        <div className="space-y-5" data-testid="rv-multi-config">
            <div className="brut-section p-3">
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <div className="label-overline">Joins (via Lookup fields)</div>
                        <p className="text-[10px] text-muted-foreground mt-1">Each join follows a lookup field on the primary form to its target form.</p>
                    </div>
                    <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={addJoin} disabled={lookupFields.length === 0} data-testid="rv-add-join">+ Join</Button>
                </div>
                {(form.joins || []).length === 0 && <div className="text-[11px] text-muted-foreground">No joins. Without a join you can still pivot/metric on the primary form alone.</div>}
                {(form.joins || []).map((j, i) => {
                    const tgt = forms.find((f) => f.id === j.target_form_id);
                    return (
                        <div key={j.__uid || `join-${i}`} className="flex items-center gap-2 text-xs mt-2 border border-border rounded-sm p-2" data-testid={`rv-join-${i}`}>
                            <span className="font-mono text-[10px] text-primary">via {j.lookup_field}</span>
                            <span className="text-muted-foreground">→</span>
                            <span className="font-medium">{tgt?.form_name || j.target_form_id}</span>
                            <span className="text-muted-foreground">as</span>
                            <code className="font-mono text-[10px] bg-secondary px-1 rounded-sm">{j.alias}</code>
                            <Button type="button" size="sm" variant="ghost" className="ml-auto h-7 text-destructive" onClick={() => removeJoin(i)} data-testid={`rv-join-rm-${i}`}>Remove</Button>
                        </div>
                    );
                })}
            </div>

            <div className="brut-section p-3">
                <div className="flex items-center justify-between mb-2">
                    <div className="label-overline">Group by (up to 2 dimensions)</div>
                    <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={addDim} disabled={(form.multi_group_by || []).length >= 2} data-testid="rv-add-dim">+ Dimension</Button>
                </div>
                {(form.multi_group_by || []).map((d, i) => (
                    <div key={d.__uid || `dim-${i}`} className="grid grid-cols-[140px_1fr_40px] gap-2 items-center mt-2" data-testid={`rv-dim-${i}`}>
                        <Select value={d.form} onValueChange={(v) => setDim(i, { form: v, field: "__created_day__" })}>
                            <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>{sources.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={d.field} onValueChange={(v) => setDim(i, { field: v })}>
                            <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__created_day__">Submission day</SelectItem>
                                <SelectItem value="__created_month__">Submission month</SelectItem>
                                {fieldsForSource(d.form).map((f) => <SelectItem key={f.field_name || f.id} value={f.field_name}>{f.field_label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => removeDim(i)} data-testid={`rv-dim-rm-${i}`}>×</Button>
                    </div>
                ))}
            </div>

            <div className="brut-section p-3">
                <div className="flex items-center justify-between mb-2">
                    <div className="label-overline">Metrics</div>
                    <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={addMetric} data-testid="rv-add-metric">+ Metric</Button>
                </div>
                {(form.multi_metrics || []).map((m, i) => {
                    const fs = fieldsForSource(m.form).filter((f) => ["number", "decimal"].includes(f.field_type));
                    return (
                        <div key={m.__uid || `metric-${i}`} className="grid grid-cols-[100px_140px_1fr_40px] gap-2 items-center mt-2" data-testid={`rv-metric-${i}`}>
                            <Select value={m.op} onValueChange={(v) => setMetric(i, { op: v, field: v === "count" ? null : m.field })}>
                                <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="count">count</SelectItem>
                                    <SelectItem value="sum">sum</SelectItem>
                                    <SelectItem value="avg">avg</SelectItem>
                                    <SelectItem value="min">min</SelectItem>
                                    <SelectItem value="max">max</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={m.form} onValueChange={(v) => setMetric(i, { form: v, field: null })}>
                                <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>{sources.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                            </Select>
                            {m.op === "count" ? (
                                <div className="text-[11px] text-muted-foreground font-mono px-2">(no field needed)</div>
                            ) : (
                                <Select value={m.field || ""} onValueChange={(v) => setMetric(i, { field: v })}>
                                    <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="Pick numeric field" /></SelectTrigger>
                                    <SelectContent>
                                        {fs.length === 0 && <SelectItem value="__none__" disabled>No numeric fields</SelectItem>}
                                        {fs.map((f) => <SelectItem key={f.field_name} value={f.field_name}>{f.field_label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            )}
                            <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => removeMetric(i)} data-testid={`rv-metric-rm-${i}`}>×</Button>
                        </div>
                    );
                })}
            </div>

            <div className="brut-section p-3">
                <div className="flex items-center justify-between mb-2">
                    <div className="label-overline">Filters (optional)</div>
                    <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={addFilter} data-testid="rv-add-filter">+ Filter</Button>
                </div>
                {(form.multi_filters || []).map((f, i) => (
                    <div key={f.__uid || `filter-${i}`} className="grid grid-cols-[120px_1fr_120px_1fr_40px] gap-2 items-center mt-2" data-testid={`rv-filter-${i}`}>
                        <Select value={f.form} onValueChange={(v) => setFilter(i, { form: v, field: "" })}>
                            <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>{sources.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={f.field} onValueChange={(v) => setFilter(i, { field: v })}>
                            <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="Field" /></SelectTrigger>
                            <SelectContent>{fieldsForSource(f.form).map((fl) => <SelectItem key={fl.field_name} value={fl.field_name}>{fl.field_label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={f.op} onValueChange={(v) => setFilter(i, { op: v })}>
                            <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="eq">equals</SelectItem>
                                <SelectItem value="neq">not equals</SelectItem>
                                <SelectItem value="contains">contains</SelectItem>
                                <SelectItem value="in">in</SelectItem>
                                <SelectItem value="gt">&gt;</SelectItem>
                                <SelectItem value="gte">≥</SelectItem>
                                <SelectItem value="lt">&lt;</SelectItem>
                                <SelectItem value="lte">≤</SelectItem>
                                <SelectItem value="is_empty">is empty</SelectItem>
                                <SelectItem value="is_not_empty">is not empty</SelectItem>
                            </SelectContent>
                        </Select>
                        <Input value={f.value ?? ""} onChange={(e) => setFilter(i, { value: e.target.value })} placeholder="value" className="rounded-sm h-9" disabled={["is_empty", "is_not_empty"].includes(f.op)} data-testid={`rv-filter-val-${i}`} />
                        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => removeFilter(i)} data-testid={`rv-filter-rm-${i}`}>×</Button>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="label-overline">Chart</Label>
                    <Select value={form.chart_type} onValueChange={(v) => setForm({ ...form, chart_type: v })}>
                        <SelectTrigger className="mt-2 rounded-sm" data-testid="rv-multi-chart-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="bar">Bar</SelectItem>
                            <SelectItem value="line">Line</SelectItem>
                            <SelectItem value="pie">Pie</SelectItem>
                            <SelectItem value="pivot">Pivot table</SelectItem>
                            <SelectItem value="kpi">KPI card</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
        </div>
    );
}